import type { StateCreator } from "zustand";
import type {
  DebateVersion,
  ComparisonReport,
  DebatePersonaSnapshot,
} from "../../domain/types";
import {
  forkDebate,
  duplicateDebate,
  loadVersionTree,
  fetchDebatePersonas,
  compareDebates,
  type ForkOptions,
} from "../../transport/authClient";

export interface VersionsSlice {
  versions: DebateVersion[];
  selectedForCompare: [string | null, string | null];
  comparisonReport: ComparisonReport | null;
  versionPersonas: DebatePersonaSnapshot[];
  /** Tree load / fork / duplicate spinner. */
  versionsLoading: boolean;
  versionsError: string | null;
  /** Distinct AI compare spinner — long-running LLM call (~15-60 s). */
  compareLoading: boolean;
  compareError: string | null;

  loadVersionTree: (debateId: string) => Promise<void>;
  forkDebate: (debateId: string, opts?: ForkOptions) => Promise<string>;
  duplicateDebate: (debateId: string) => Promise<string>;
  selectForCompare: (debateId: string) => void;
  clearCompareSelection: () => void;
  compareDebates: (aId: string, bId: string, force?: boolean) => Promise<void>;
  loadDebatePersonas: (debateId: string) => Promise<void>;
  resetVersions: () => void;
}

export const createVersionsSlice: StateCreator<
  VersionsSlice,
  [["zustand/immer", never]],
  [],
  VersionsSlice
> = (set) => ({
  versions: [],
  selectedForCompare: [null, null],
  comparisonReport: null,
  versionPersonas: [],
  versionsLoading: false,
  versionsError: null,
  compareLoading: false,
  compareError: null,

  loadVersionTree: async (debateId: string) => {
    set((s) => {
      s.versionsLoading = true;
      s.versionsError = null;
    });
    try {
      const res = await loadVersionTree(debateId);
      set((s) => {
        s.versions = res.tree;
        s.versionsLoading = false;
      });
    } catch (e) {
      set((s) => {
        s.versionsError = e instanceof Error ? e.message : String(e);
        s.versionsLoading = false;
      });
    }
  },

  forkDebate: async (debateId: string, opts?: ForkOptions) => {
    set((s) => {
      s.versionsLoading = true;
    });
    try {
      const res = await forkDebate(debateId, opts);
      set((s) => {
        s.versionsLoading = false;
      });
      return res.debate_id;
    } catch (e) {
      set((s) => {
        s.versionsError = e instanceof Error ? e.message : String(e);
        s.versionsLoading = false;
      });
      throw e;
    }
  },

  duplicateDebate: async (debateId: string) => {
    set((s) => {
      s.versionsLoading = true;
    });
    try {
      const res = await duplicateDebate(debateId);
      set((s) => {
        s.versionsLoading = false;
      });
      return res.debate_id;
    } catch (e) {
      set((s) => {
        s.versionsError = e instanceof Error ? e.message : String(e);
        s.versionsLoading = false;
      });
      throw e;
    }
  },

  selectForCompare: (debateId: string) => {
    set((s) => {
      const [a, b] = s.selectedForCompare;
      if (a === debateId || b === debateId) return;
      if (!a) {
        s.selectedForCompare = [debateId, b];
      } else if (!b) {
        s.selectedForCompare = [a, debateId];
      } else {
        s.selectedForCompare = [debateId, null];
      }
    });
  },

  clearCompareSelection: () => {
    set((s) => {
      s.selectedForCompare = [null, null];
      s.comparisonReport = null;
    });
  },

  compareDebates: async (aId: string, bId: string, force?: boolean) => {
    set((s) => {
      s.compareLoading = true;
      s.compareError = null;
    });
    try {
      const report = await compareDebates(aId, bId, force);
      set((s) => {
        s.comparisonReport = report;
        s.compareLoading = false;
      });
    } catch (e) {
      set((s) => {
        s.compareError = e instanceof Error ? e.message : String(e);
        s.compareLoading = false;
      });
    }
  },

  loadDebatePersonas: async (debateId: string) => {
    try {
      const personas = await fetchDebatePersonas(debateId);
      set((s) => {
        s.versionPersonas = personas;
      });
    } catch (e) {
      set((s) => {
        s.versionsError = e instanceof Error ? e.message : String(e);
      });
    }
  },

  resetVersions: () => {
    set((s) => {
      s.versions = [];
      s.selectedForCompare = [null, null];
      s.comparisonReport = null;
      s.versionPersonas = [];
      s.versionsLoading = false;
      s.versionsError = null;
      s.compareLoading = false;
      s.compareError = null;
    });
  },
});
